import React, { useState, FormEvent, useRef } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import "primeflex/primeflex.css";
import { VirtualScroller } from "primereact/virtualscroller";
import { useToast } from "../../utils/toast";
import { useAuth } from "../../utils/auth";
import aiChatService from "../../services/ai-chat/ai-chat.service";

interface Message {
  id: string;
  sender: "user" | "bot";
  text: string;
}

interface GeminiResponse {
  candidates?: {
    content: {
      parts: {
        text: string;
      }[];
    };
  }[];
  modelVersion?: string;
  responseId?: string;
  usageMetadata?: {
    promptTokenCount: number;
    candidatesTokenCount: number;
    totalTokenCount: number;
  };
}

// Markdown formatlamasını HTML'e çeviren yardımcı fonksiyon
const convertMarkdownToHtml = (text: string): string => {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // **text** -> <strong>text</strong>
    .replace(/\*(.*?)\*/g, '<em>$1</em>') // *text* -> <em>text</em>
    .replace(/`(.*?)`/g, '<code>$1</code>') // `text` -> <code>text</code>
    .replace(/\n/g, '<br />'); // Yeni satırları <br /> ile değiştir
};

const SoruCevapSayfası: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState<string>("");
  const [buttonIsDisabled, setButtonIsDisabled] = useState<boolean>(false);
  const toast = useToast();
  const auth = useAuth();
  const scrollRef = useRef<VirtualScroller>(null);

  const [isLoading, setIsLoading] = useState(false);

  // Backend'e mesaj kaydetme fonksiyonu
  const saveMessageToBackend = async (question: string, answer: string, responseTime: number, modelVersion: string) => {
    try {
      console.log('Saving message to backend:', { question, answer, responseTime, modelVersion });
      
      const result = await aiChatService.SaveChatMessage({
        question: question,
        answer: answer,
        ai_model: modelVersion,
        response_time: responseTime
      });

      console.log('Backend response:', result);
      
      if (result.result?.data) {
        console.log('Backend kaydetme başarılı:', result.result.data);
      } else {
        console.error('Backend kaydetme hatası:', result.result);
      }
    } catch (error) {
      console.error('Backend kaydetme hatası:', error);
    }
  };

  const sendMessage = async () => {
    const cleanedInput = inputMessage.trim();
    if (!cleanedInput) {
      setButtonIsDisabled(false);
      toast.show("Lütfen geçerli bir mesaj girin.", "error");
      return;
    }

    setIsLoading(true); // Start loading
    const userMessage: Message = {
      id: Date.now().toString(),
      sender: "user",
      text: inputMessage
    };
    setInputMessage("");

    setMessages((prevMessages) => [...prevMessages, userMessage]);

    try {
      const startTime = Date.now();
      const { botResponse, modelVersion } = await handleButtonClick(cleanedInput);
      const responseTime = (Date.now() - startTime) / 1000; // saniye cinsinden
      
      const botMessage: Message = {
        id: Date.now().toString(),
        sender: "bot",
        text: botResponse
      };
      setMessages((prevMessages) => [...prevMessages, botMessage]);

      // Backend'e kaydet
      await saveMessageToBackend(cleanedInput, botResponse, responseTime, modelVersion);
      
    } catch (error) {
      console.error("Error during bot response:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const handleButtonClick = async (message: string): Promise<{ botResponse: string; modelVersion: string }> => {
    const url =
        "https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash-latest:generateContent?key=AIzaSyDkUuRe_Jxit2qQuA6VwjSfbUNXOADFhFI";
        // "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-pro:generateContent?key=AIzaSyDXqem-lu8HPBoShLZX4WkLr-ewWm11rBs";
    //   "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=AIzaSyDXqem-lu8HPBoShLZX4WkLr-ewWm11rBs";

    // Sistem prompt'u - yapay zekanın nasıl davranması gerektiğini belirtir
    const requestBody = {
      contents: [
        {
          role: "user",
          parts: [
            {
              text: "Sen, saygılı, profesyonel ve destekleyici bir yapay zeka asistanısın. Her zaman nazik ve resmi bir üslupla cevap ver. Türkçe dilbilgisi kurallarına uygun, resmi dilde cevaplar sun. Eğer bir konuda emin değilsen, bilgi eksikliğini nazikçe ve resmi bir şekilde ifade edin."
            }
          ]
        },
        {
          role: "user",
          parts: [
            {
              text: message
            }
          ]
        }
      ]
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data: GeminiResponse = await response.json();
      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content &&
        data.candidates[0].content.parts &&
        data.candidates[0].content.parts.length > 0
      ) {
        const botResponse = data.candidates[0].content.parts[0].text;
        const modelVersion = data.modelVersion || 'gemini-2.5-pro';
        return { botResponse, modelVersion };
      } else {
        throw new Error("Unexpected API response format");
      }
    } catch (error) {
      toast.show(`Bir hata oluştu.`, "error");
      return { botResponse: "Bir hata oluştu.", modelVersion: 'gemini-2.5-pro' };
    }
  };

  const renderMessages = () => {
    return (
      <VirtualScroller
        items={messages}
        itemSize={150}
        className="mb-5"
        itemTemplate={(message: Message) => {
          return (
            <div key={message.id} className={`flex ${message.sender === "user" ? "flex-row-reverse" : ""}`}>
              <div className="card my-1 border-2 border-primary-100" style={{ width: "80%", padding: "0.5rem" }}>
                <div style={{ wordBreak: "break-word", marginBottom: "0.2rem" }}>
                  <span className="font-bold text-lg">{message.sender === "user" ? "Ben" : "Yapay Zeka"}:</span> <br />
                  {message.sender === "bot" ? (
                    <div 
                      style={{ display: "block", marginTop: "0.2rem" }}
                      dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(message.text) }}
                    />
                  ) : (
                    <span style={{ display: "block", marginTop: "0.2rem" }}>{message.text}</span>
                  )}
                </div>
              </div>
            </div>
          );
        }}
      />
    );
  };

  return (
    <div className="card flex flex-column" style={{ height: "77vh" }}>
      {/* Üstteki Örnek Buton */}
      <div className="pt-0 mt-0 pl-0 ml-0">
        <Button
          label="Yeni Sohbet"
          onClick={() => {
            setMessages([]);
            setInputMessage("");
          }}
        />
      </div>

      {renderMessages()}

      {/* Giriş Alanı */}
      <form
        className="flex gap-2 align-items-center mb-0 pb-0"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          sendMessage();
        }}
      >
        <div className="flex-grow-1">
          <InputText
            id="searchWord"
            name="searchWord"
            className="w-full"
            placeholder="Mesajınızı buraya yazın"
            value={inputMessage}
            style={{ height: "60px" }}
            onChange={(e) => setInputMessage(e.target.value)}
            disabled={buttonIsDisabled}
          />
        </div>
        <div style={{ flexShrink: 0 }}>
          <Button
            label={isLoading ? "Yükleniyor..." : "Gönder"}
            className="w-full"
            style={{ height: "60px" }}
            type="submit"
            disabled={buttonIsDisabled || inputMessage.trim() === ""}
          />
        </div>
      </form>
    </div>
  );
};

export default SoruCevapSayfası;
